import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledFormikField } from '../../../base/forms/input';
import { P } from '../../../base/typography';
import UploadFiles from '../../../uploadFiles';
import { useMultiStepFormContext } from '../../context/multistepContext';
import { useTranslationsContext } from '../../context/translationsContext';
import { FormStepProps } from '../../sendReceiptMultistep';
import {
	ButtonWrapper,
	ErrorMessage,
	FormStepHeading,
	Label,
	PrevButton,
	PrevButtonMobile,
	ProceedButton,
} from '../../sendReceiptMultistep.styles';
import { INSTRUCTIONS_TO_STAFF_STEP_INDEX, SEPARATOR_CHAR } from './InstructionToStaff';
import * as StyledType from './InstructionToStaff.styles';

const TRACKING_NUMBER_MAX_LENGTH = 13;

export default function ShipmentClearedByCustom({ values, setFieldTouched, touched, errors }: FormStepProps) {
	const translations = useTranslationsContext();
	const { next, prev } = useMultiStepFormContext();
	const { t } = useTranslation('sendReceiptMultistep');

	useEffect(() => {
		if (values.files.length !== 0) {
			setFieldTouched('files', true);
		}
	}, [values, setFieldTouched]);

	const isStepComplete =
		values.clearedTrackingNumber &&
		values.additionalInformation &&
		!errors.clearedTrackingNumber &&
		!errors.files &&
		!errors.additionalInformation;

	return (
		<>
			<PrevButtonMobile
				onClick={e => {
					e.stopPropagation();
					prev();
				}}
				>
				{translations?.prevStepMobile || t('prevStepMobile')}
			</PrevButtonMobile>
			<FormStepHeading>
				{translations?.formStepTitles?.type?.alreadyCleared || t('formStepTitles.type.alreadyCleared')}
			</FormStepHeading>
			<P>{translations?.alreadyCleared?.shipmentClearedText || t('alreadyCleared.shipmentClearedText')}</P>
			<StyledType.FieldWrapper>
				<Label marginTop="2rem">
					{translations?.alreadyCleared?.shipmentClearedTrackingNumber ||
						t('alreadyCleared.shipmentClearedTrackingNumber')}
				</Label>
				<StyledFormikField
					name="clearedTrackingNumber"
					id="clearedTrackingNumber"
					type="text"
					placeholder={
						translations?.alreadyCleared?.shipmentClearedTrackingNumberPlaceholder ||
						t('alreadyCleared.shipmentClearedTrackingNumberPlaceholder')
					}
				/>
				{touched.clearedTrackingNumber && errors.clearedTrackingNumber && (
					<ErrorMessage>
						{translations?.alreadyCleared?.errors[errors.clearedTrackingNumber] ||
							t(`alreadyCleared.errors.${errors.clearedTrackingNumber}`)}
					</ErrorMessage>
				)}
			</StyledType.FieldWrapper>
			<P>{translations?.validReceipt || t('validReceipt')}</P>
			<StyledType.FieldWrapper>
				<Label>{translations?.sendAFile || t('sendAFile')}*</Label>
				<UploadFiles name="files" />
				{touched.files && errors.files && (
					<ErrorMessage>
						{translations?.alreadyCleared.errors[
							errors.files as keyof typeof translations.alreadyCleared.errors
						] || t(`alreadyCleared.errors.${errors.files}`)}
					</ErrorMessage>
				)}
			</StyledType.FieldWrapper>
			<StyledType.FieldWrapper>
				<Label>
					{translations?.description || t('description')}
					*
				</Label>
				<StyledType.TextArea
					maxLength={254 - TRACKING_NUMBER_MAX_LENGTH - SEPARATOR_CHAR.length}
					type="text"
					id="additionalInformation"
					name="additionalInformation"
					component="textarea"
					padding="1rem"
					placeholder={`${translations?.descriptionPlaceholder || t('descriptionPlaceholder')}`}
				/>
				{touched.additionalInformation && errors.additionalInformation && (
					<ErrorMessage>
						{translations?.alreadyCleared.errors[
							errors.additionalInformation as keyof typeof translations.alreadyCleared.errors
						] || t(`alreadyCleared.errors.${errors.additionalInformation}`)}
					</ErrorMessage>
				)}
			</StyledType.FieldWrapper>
			<ButtonWrapper>
				<PrevButton
					onClick={e => {
						e.stopPropagation();
						prev();
					}}
					>
					{translations?.prevStep || t('prevStep')}
				</PrevButton>
				<ProceedButton
					type="button"
					onClick={e => {
						e.stopPropagation();
						next();
					}}
					disabled={!isStepComplete}>
					{translations?.nextStep || t('nextStep')}
				</ProceedButton>
			</ButtonWrapper>
		</>
	);
}
